package sarka.design.skoria.presentation

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import sarka.design.skoria.friends.Friend
import sarka.design.skoria.friends.FriendApi
import sarka.design.skoria.friends.FriendError
import sarka.design.skoria.friends.IncomingRequest
import sarka.design.skoria.friends.OutgoingRequest
import sarka.design.skoria.messaging.ConversationView
import sarka.design.skoria.messaging.MessageRepository

/**
 * A friend paired with the preview of the last message exchanged, when one
 * exists. The friends list and the conversation list are the same screen now:
 * you can only message friends, so a friend row doubles as a chat entry.
 */
data class FriendRow(
    val username: String,
    val since: Long,
    val lastMessagePreview: String? = null,
    val lastMessageFromMe: Boolean = false,
    val lastMessageAt: Long? = null,
    val unreadCount: Int = 0,
) {
    /** Ordering value: recent message time if any, otherwise friendship time. */
    val sortKey: Long get() = lastMessageAt ?: since
}

data class ConversationListUiState(
    val friends: List<FriendRow> = emptyList(),
    val incoming: List<IncomingRequest> = emptyList(),
    val outgoing: List<OutgoingRequest> = emptyList(),
    val isLoading: Boolean = false,
    val error: String? = null,
    /** Username typed into the "add friend" field. */
    val addFriendUsername: String = "",
    val addFriendError: String? = null,
    /** A transient confirmation, e.g. "Request sent". Cleared on next action. */
    val notice: String? = null,
)

/**
 * Backs the friends-and-chats screen.
 *
 * Merges two server views: the friend graph (friends, incoming/outgoing
 * requests) and the message conversation previews. The friend graph decides who
 * appears; the conversation data only decorates a friend row with its last
 * message.
 */
class ConversationListViewModel(
    private val repository: MessageRepository,
    private val friendApi: FriendApi = FriendApi(),
) : ViewModel() {

    private val _state = MutableStateFlow(ConversationListUiState())
    val state: StateFlow<ConversationListUiState> = _state.asStateFlow()

    init {
        refresh()

        // Keep the socket open while the list is on screen, so a message arriving
        // updates the previews and unread counts without a manual refresh.
        repository.startListening()

        viewModelScope.launch {
            repository.incoming.collect { refresh() }
        }

        // Receipts can change a preview row's state (e.g. a message we sent is
        // now read), so refresh on those too while the list is on screen.
        viewModelScope.launch {
            repository.receipts.collect { refresh() }
        }
    }

    fun refresh() {
        _state.update { it.copy(isLoading = true, error = null) }

        viewModelScope.launch {
            val overviewResult = friendApi.overview()
            val conversationsResult = repository.conversations()

            overviewResult
                .onSuccess { overview ->
                    val previews: Map<String, ConversationView> =
                        conversationsResult.getOrNull()
                            ?.associateBy { it.username.lowercase() }
                            ?: emptyMap()

                    val rows = overview.friends.map { friend ->
                        friend.toRow(previews[friend.username.lowercase()])
                    }.sortedWith(
                        // Unread first, then most-recently-active, then newest
                        // friendship. sortKey is the last message time when there
                        // is one, otherwise when the friendship formed.
                        compareByDescending<FriendRow> { it.unreadCount > 0 }
                            .thenByDescending { row -> row.sortKey }
                    )

                    _state.update {
                        it.copy(
                            isLoading = false,
                            friends = rows,
                            incoming = overview.incoming,
                            outgoing = overview.outgoing,
                            error = null,
                        )
                    }
                }
                .onFailure { e ->
                    _state.update { it.copy(isLoading = false, error = e.readable()) }
                }
        }
    }

    fun onAddFriendUsernameChange(value: String) {
        _state.update { it.copy(addFriendUsername = value, addFriendError = null, notice = null) }
    }

    /** Sends a friend request to the typed username. */
    fun sendFriendRequest() {
        val target = _state.value.addFriendUsername.lowercase().trim()
        if (target.isBlank()) {
            _state.update { it.copy(addFriendError = "Enter a username") }
            return
        }

        _state.update { it.copy(addFriendError = null, notice = null) }

        viewModelScope.launch {
            friendApi.sendRequest(target)
                .onSuccess {
                    _state.update { it.copy(addFriendUsername = "", notice = "Request sent to $target") }
                    refresh()
                }
                .onFailure { e ->
                    _state.update { it.copy(addFriendError = e.readable()) }
                }
        }
    }

    fun acceptRequest(fromUsername: String) = act { friendApi.accept(fromUsername) }
    fun declineRequest(fromUsername: String) = act { friendApi.decline(fromUsername) }
    fun cancelRequest(toUsername: String) = act { friendApi.cancel(toUsername) }
    fun unfriend(username: String) = act { friendApi.unfriend(username) }
    fun block(username: String) = act { friendApi.block(username) }

    /** Runs a friend action then refreshes, surfacing any error. */
    private fun act(action: suspend () -> Result<Unit>) {
        viewModelScope.launch {
            action()
                .onSuccess { refresh() }
                .onFailure { e -> _state.update { it.copy(error = e.readable()) } }
        }
    }

    fun clearNotice() {
        _state.update { it.copy(notice = null) }
    }

    private fun Friend.toRow(preview: ConversationView?): FriendRow = FriendRow(
        username = username,
        since = since,
        lastMessagePreview = preview?.preview,
        lastMessageFromMe = preview?.lastMessageFromMe ?: false,
        lastMessageAt = preview?.lastMessageAt,
        unreadCount = preview?.unreadCount ?: 0,
    )

    private fun Throwable.readable(): String = when (this) {
        is FriendError.UserNotFound -> "No user called $username"
        is FriendError.Conflict -> message ?: "Can't send that request"
        is FriendError.RateLimited -> "You're doing that too quickly. Please slow down."
        is FriendError.NotAuthenticated -> "Session expired. Please log in again."
        is FriendError.Offline -> "Could not reach the server"
        else -> message ?: "Something went wrong"
    }
}
